import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { combineLatest, filter, map, switchMap, take, tap } from 'rxjs';

import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { LaunchDarklyService } from 'src/app/services/launchdarkly.service';
import { UpdateService } from 'src/app/services/update.service';

import { LoginService } from './login.service';

/**
 * Login page.
 */
@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	imports: [ProgressSpinnerModule],
	providers: [LoginService, UpdateService],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
	readonly showShutdown = signal(false);

	/**
	 * Observable of origin.
	 * Gets origin from query params if available, then saves to local storage,
	 * which is used as backup after navigation.
	 */
	private origin$ = this.activatedRoute.queryParamMap.pipe(
		map(queryParams => queryParams.get('origin')),
		tap(origin => {
			if (origin) {
				localStorage.setItem('origin', origin);
				localStorage.setItem('originTimestamp', Date.now().toString());
			}
		}),
		map(origin => {
			if (origin) {
				return origin;
			}

			// Fallback to localStorage only if recent (within 5 minutes).
			const storedOrigin = localStorage.getItem('origin');
			const storedTimestamp = localStorage.getItem('originTimestamp');

			if (storedOrigin && storedTimestamp) {
				const age = Date.now() - parseInt(storedTimestamp);
				if (age < 5 * 60 * 1000) {
					return storedOrigin;
				}
				// Clear stale origin.
				localStorage.removeItem('origin');
				localStorage.removeItem('originTimestamp');
			}
			return '';
		}),
		map(origin => {
			// Ensure that orgin parameters that may be incorrect are removed from the origin URL.
			if (origin && origin.toLowerCase() !== 'windows') {
				const url = new URL(origin);
				url.searchParams.delete('sessionId');
				url.searchParams.delete('username');
				url.searchParams.delete('company');
				return url.toString();
			} else {
				return origin;
			}
		}),
	);

	/**
	 * Observable of auth code.
	 */
	private authCode$ = this.activatedRoute.queryParamMap.pipe(map(queryParams => queryParams.get('code') ?? ''));

	/**
	 * Observable of origin directly from the URL, without localStorage fallback.
	 * Used to gate the sourceApplication check so stale localStorage does not interfere.
	 */
	private originParam$ = this.activatedRoute.queryParamMap.pipe(map(queryParams => queryParams.get('origin') ?? ''));

	/**
	 * Observable of source application.
	 */
	private sourceApplication$ = this.activatedRoute.queryParamMap.pipe(map(queryParams => queryParams.get('sourceApplication')));

	/**
	 * Constructor.
	 * @param loginService To subscribe to user session observable.
	 * @param activatedRoute To observe query parameters.
	 * @param updateService To check for client updates.
	 * @param launchDarklyService
	 */
	constructor(
		private loginService: LoginService,
		private activatedRoute: ActivatedRoute,
		private updateService: UpdateService,
		private launchDarklyService: LaunchDarklyService,
	) {}

	/**
	 * On init, subscribe to user session observable to handle user session creation/verification.
	 */
	ngOnInit() {
		combineLatest([this.originParam$, this.sourceApplication$, this.launchDarklyService.launchDarklyFlags$.pipe(take(1))])
			.pipe(
				tap(([originParam, sourceApplication, flags]) => {
					if (
						!flags['is-test-linker-desktop-live'] &&
						originParam.toLowerCase() === 'windows' &&
						sourceApplication?.toLowerCase() !== 'ecucompare' &&
						sourceApplication?.toLowerCase() !== 'testplanner'
					) {
						this.showShutdown.set(true);
					}
				}),
				filter(
					([originParam, sourceApplication]) =>
						!(originParam.toLowerCase() === 'windows' && sourceApplication?.toLowerCase() !== 'ecucompare' && sourceApplication?.toLowerCase() !== 'testplanner'),
				),
				// First, check for updates, and reload page to ensure latest client version is installed if necessary.
				switchMap(() => this.updateService.updatesAvailable$()),
				tap(updateAvailable => {
					if (updateAvailable) {
						document.location.reload();
					}
				}),
				switchMap(() => this.loginService.handleUserSession$(this.origin$, this.authCode$)),
			)
			.subscribe();
	}
}
